import React from 'react';
import {
    FiActivity,
    FiAlertTriangle,
    FiClock,
    FiCpu,
    FiHardDrive,
    FiPlay,
    FiThermometer,
    FiWifi,
    FiZap,
} from 'react-icons/fi';

import {
    cpuTempTone,
    crashesTone,
    getFreshness,
    healthStatusTone,
    humanizeAge,
    latencyTone,
    packetLossTone,
    ramTone,
    storageTone,
    toneClasses,
} from '../../utils/deviceData';

// HealthTab — the full Phase-2 telemetry surface (DEVICE-DATA.md §3).
//
// One score gauge up top, one card per cluster of metrics below. Every
// optional field hides its row when missing rather than rendering 0 or
// N/A — DEVICE-DATA.md §6 explicitly says omitted ≠ zero.

export default function HealthTab({ device }) {
    const dd = device?.device_data;

    if (!dd) {
        // No device_data ever recorded — pre-3.0 device or first boot
        // (DEVICE-DATA.md §6).
        return (
            <div className="rounded-xl border border-dashed border-white/[0.08] p-10 text-center text-gray-400">
                <FiClock className="mx-auto text-3xl mb-3 text-gray-500" />
                <p className="text-sm">
                    Health telemetry hasn't arrived yet. The monitoring service pushes
                    every 10 minutes — this is normal on a freshly-installed device.
                </p>
            </div>
        );
    }

    const fresh = getFreshness(dd.updated_at);

    return (
        <div className="space-y-6">
            {/* Score + freshness on the left, big gauge on the right */}
            <ScoreHero device_data={dd} freshness={fresh} />

            {/* Detail cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <StorageCard dd={dd} />
                <RamCard dd={dd} />
                <UptimeCard dd={dd} />
                <StabilityCard dd={dd} />
                <NetworkCard dd={dd} />
                <PerformanceCard dd={dd} />
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------
// Score hero — the eye-catching part. SVG gauge ring + score number.
// ---------------------------------------------------------------------

function ScoreHero({ device_data: dd, freshness }) {
    const score = dd.health_score ?? null;
    const tone = healthStatusTone(dd.health_status);
    const c = toneClasses(tone);

    return (
        <section className="card-chrome p-6 flex flex-col md:flex-row items-center gap-6">
            <ScoreGauge score={score} colorHex={c.hex} />
            <div className="flex-1 min-w-0">
                <h2 className="text-xs font-medium tracking-wider text-gray-400 uppercase">Health Score</h2>
                <div className="mt-1 flex items-baseline gap-3">
                    <span className={`text-4xl font-bold ${c.text}`}>{score ?? '—'}</span>
                    <span className={`text-sm font-medium ${c.text}`}>{dd.health_status || 'unknown'}</span>
                </div>
                <p
                    className="mt-3 text-sm text-gray-400 max-w-xl"
                    title={SCORE_FORMULA_TEXT}
                >
                    Composite 0–100 score; deductions for stability, storage,
                    memory, network, and thermal issues. Hover for the full formula.
                </p>
                <p className="mt-3 text-xs text-gray-500 inline-flex items-center gap-1.5">
                    <FiClock />
                    {freshness.isMissing ? 'no fresh telemetry' : `updated ${freshness.label}`}
                    {freshness.isStale && (
                        <span className="ml-2 px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400 text-[11px] font-medium">
                            stale
                        </span>
                    )}
                </p>
            </div>
        </section>
    );
}

// SCORE_FORMULA_TEXT mirrors DEVICE-DATA.md §4. Used as a tooltip so the
// "where did this number come from" question has an in-product answer.
const SCORE_FORMULA_TEXT = [
    'Starts at 100, deducts cumulatively:',
    '• crashes_24h > 5         → -20',
    '• storage_free_pct < 15   → -15',
    '• storage_free_pct 15–30  → -7',
    '• ram_free_mb < 200       → -10',
    '• latency_ms > 500        → -10',
    '• packet_loss > 5%        → -10',
    '• cpu_temp_c > 80         → -10',
    '• cpu_usage > 90%         → -5',
    '• uptime_hours < 1        → -5  (just rebooted)',
    '• boot_count_24h > 3      → -5  (frequent reboots)',
    '',
    '80–100 good · 60–79 fair · 40–59 warning · <40 critical',
].join('\n');

// Round dial. Pure SVG, ~120px diameter.
function ScoreGauge({ score, colorHex }) {
    const safeScore = Math.max(0, Math.min(100, Number.isFinite(score) ? score : 0));
    const RADIUS = 52;
    const STROKE = 10;
    const C = 2 * Math.PI * RADIUS;
    // Negative offset makes the ring fill clockwise from 12 o'clock.
    const offset = C * (1 - safeScore / 100);

    return (
        <div className="relative" style={{ width: 132, height: 132 }}>
            <svg width={132} height={132} viewBox="0 0 132 132">
                {/* Track */}
                <circle
                    cx={66} cy={66} r={RADIUS}
                    fill="none" stroke="#1f2937" strokeWidth={STROKE}
                />
                {/* Fill */}
                <circle
                    cx={66} cy={66} r={RADIUS}
                    fill="none" stroke={colorHex} strokeWidth={STROKE}
                    strokeLinecap="round"
                    strokeDasharray={C}
                    strokeDashoffset={offset}
                    transform="rotate(-90 66 66)"
                    style={{ transition: 'stroke-dashoffset 600ms ease' }}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold" style={{ color: colorHex }}>
                    {score ?? '—'}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-gray-500">score</span>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------
// Detail cards — small, focused, colour-coded.
// ---------------------------------------------------------------------

function MetricCard({ icon, title, tone, children, footer }) {
    const c = toneClasses(tone);
    return (
        <section className={`card-chrome p-5 ring-1 ${c.ring}`}>
            <header className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-medium uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                    <span className={c.text}>{icon}</span>
                    {title}
                </h3>
            </header>
            <div className="space-y-2">{children}</div>
            {footer && <div className="mt-3 pt-3 border-t border-white/[0.06] text-xs text-gray-500">{footer}</div>}
        </section>
    );
}

function MetricRow({ label, value, tone }) {
    if (value === undefined || value === null || value === '') return null;
    const c = toneClasses(tone || 'neutral');
    return (
        <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-gray-400">{label}</span>
            <span className={`font-medium ${c.text}`}>{value}</span>
        </div>
    );
}

function StorageCard({ dd }) {
    if (dd.storage_total_gb == null && dd.storage_free_gb == null) return null;
    const pctFree = dd.storage_total_gb && dd.storage_free_gb != null
        ? (dd.storage_free_gb / dd.storage_total_gb) * 100
        : null;
    const tone = storageTone(pctFree) || (dd.storage_health === 'good' ? 'good' : dd.storage_health === 'warning' ? 'warning' : dd.storage_health === 'critical' ? 'critical' : 'neutral');
    return (
        <MetricCard icon={<FiHardDrive />} title="Storage" tone={tone}>
            <MetricRow label="Free" value={dd.storage_free_gb != null ? `${dd.storage_free_gb.toFixed(2)} GB` : null} tone={tone} />
            <MetricRow label="Total" value={dd.storage_total_gb != null ? `${dd.storage_total_gb.toFixed(2)} GB` : null} />
            {pctFree != null && <MetricRow label="% free" value={`${pctFree.toFixed(1)}%`} tone={tone} />}
            <MetricRow label="Health" value={dd.storage_health} tone={tone} />
        </MetricCard>
    );
}

function RamCard({ dd }) {
    if (dd.ram_total_mb == null && dd.ram_free_mb == null) return null;
    const pctFree = dd.ram_total_mb && dd.ram_free_mb != null
        ? (dd.ram_free_mb / dd.ram_total_mb) * 100
        : null;
    const tone = ramTone(pctFree);
    return (
        <MetricCard icon={<FiCpu />} title="Memory" tone={tone}>
            <MetricRow label="Free" value={dd.ram_free_mb != null ? `${dd.ram_free_mb} MB` : null} tone={tone} />
            <MetricRow label="Total" value={dd.ram_total_mb != null ? `${dd.ram_total_mb} MB` : null} />
            {pctFree != null && <MetricRow label="% free" value={`${pctFree.toFixed(1)}%`} tone={tone} />}
        </MetricCard>
    );
}

function UptimeCard({ dd }) {
    if (dd.uptime_hours == null && dd.last_boot_time == null && dd.boot_reason == null) return null;
    const lastBootAge = dd.last_boot_time ? humanizeAge(Date.now() - Number(dd.last_boot_time)) : null;
    return (
        <MetricCard icon={<FiZap />} title="Uptime">
            <MetricRow label="Hours" value={dd.uptime_hours != null ? String(dd.uptime_hours) : null} />
            <MetricRow label="Last boot" value={lastBootAge} />
            <MetricRow label="Reason" value={dd.boot_reason} />
            <MetricRow label="Boots (24h)" value={dd.boot_count_24h != null ? String(dd.boot_count_24h) : null} />
        </MetricCard>
    );
}

function StabilityCard({ dd }) {
    if (dd.crashes_24h == null && dd.player_runtime_minutes == null) return null;
    const tone = crashesTone(dd.crashes_24h);
    return (
        <MetricCard icon={dd.crashes_24h > 0 ? <FiAlertTriangle /> : <FiPlay />} title="Stability" tone={tone}>
            <MetricRow label="Crashes (24h)" value={dd.crashes_24h != null ? String(dd.crashes_24h) : null} tone={tone} />
            <MetricRow label="Player runtime" value={dd.player_runtime_minutes != null ? `${dd.player_runtime_minutes} min` : null} />
        </MetricCard>
    );
}

function NetworkCard({ dd }) {
    const nq = dd.network_quality;
    if (!nq && !dd.network_type) return null;
    const latencyT = latencyTone(nq?.latency_ms);
    const lossT = packetLossTone(nq?.packet_loss_percent);
    return (
        <MetricCard icon={<FiWifi />} title="Network" tone={latencyT}>
            <MetricRow label="Type" value={dd.network_type} />
            <MetricRow label="Latency" value={nq?.latency_ms != null ? `${nq.latency_ms} ms` : null} tone={latencyT} />
            <MetricRow label="Packet loss" value={nq?.packet_loss_percent != null ? `${nq.packet_loss_percent}%` : null} tone={lossT} />
        </MetricCard>
    );
}

function PerformanceCard({ dd }) {
    const p = dd.performance;
    if (!p) return null; // Whole card hides when block absent (DEVICE-DATA.md §6)
    const tempT = cpuTempTone(p.cpu_temperature_c);
    return (
        <MetricCard icon={<FiThermometer />} title="Performance" tone={tempT}>
            <MetricRow label="CPU usage" value={p.cpu_usage_percent != null ? `${p.cpu_usage_percent}%` : null} />
            <MetricRow label="CPU temp" value={p.cpu_temperature_c != null ? `${p.cpu_temperature_c}°C` : null} tone={tempT} />
        </MetricCard>
    );
}
